import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';

import Icon from '../../Icon';
import type { SlashItem } from './slashTypes';

type SlashMenuView = 'root' | 'new-field' | 'existing-field';

const groupOrder = ['QUESTIONS', 'STRUCTURE & LAYOUT', 'TEXT STYLING', 'MEDIA'];

type SlashItemGroup = {
  group: string;
  items: SlashItem[];
};

const groupSlashItems = (items: SlashItem[]): SlashItemGroup[] => {
  const groups = new Map<string, SlashItem[]>();
  items.forEach(item => {
    const group = item.group ?? 'Blocks';
    const list = groups.get(group) ?? [];
    list.push(item);
    groups.set(group, list);
  });

  return Array.from(groups.entries())
    .sort((a, b) => {
      const aIndex = groupOrder.indexOf(a[0]);
      const bIndex = groupOrder.indexOf(b[0]);
      if (aIndex === -1 && bIndex === -1) return 0;
      if (aIndex === -1) return 1;
      if (bIndex === -1) return -1;
      return aIndex - bIndex;
    })
    .map(([group, groupItems]) => ({ group, items: groupItems }));
};

export type SlashMenuHandle = {
  onKeyDown: (event: KeyboardEvent) => boolean;
};

export type SlashMenuProps = {
  items: SlashItem[];
  query: string;
  command: (item: SlashItem) => void;
};

export const SlashSearchPill = ({ query }: { query: string }) => {
  const normalizedQuery = query.trim();
  return (
    <div className='slash-menu__search'>
      <span className='slash-menu__search-slash'>/</span>
      <span className={['slash-menu__search-text', normalizedQuery && 'is-query'].filter(Boolean).join(' ')}>
        {normalizedQuery || 'Search…'}
      </span>
    </div>
  );
};

const isSubmenuTriggerItem = (id: string) => id === 'add-new-field' || id === 'use-existing-field';

const resolveOpenSubmenu = (menuView: SlashMenuView, itemId: string) =>
  (menuView === 'new-field' && itemId === 'add-new-field') ||
  (menuView === 'existing-field' && itemId === 'use-existing-field');

export const SlashMenu = forwardRef<SlashMenuHandle, SlashMenuProps>(({ items, query, command }, ref) => {
  const [menuView, setMenuView] = useState<SlashMenuView>('root');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const normalizedQuery = query ?? '';

  const rootItems = useMemo(() => items.filter(item => (item.scope ?? 'root') === 'root'), [items]);
  const newFieldItems = useMemo(() => items.filter(item => item.scope === 'new-field'), [items]);
  const existingFieldItems = useMemo(() => items.filter(item => item.scope === 'existing-field'), [items]);
  const hasNewFieldItems = newFieldItems.length > 0;
  const hasExistingFieldItems = existingFieldItems.length > 0;
  const activeMenuView = useMemo(() => {
    if (menuView === 'new-field' && !hasNewFieldItems) return 'root';
    if (menuView === 'existing-field' && !hasExistingFieldItems) return 'root';
    return menuView;
  }, [menuView, hasNewFieldItems, hasExistingFieldItems]);

  const activeItems = useMemo(() => {
    if (normalizedQuery) return items;
    if (activeMenuView === 'new-field') return newFieldItems;
    if (activeMenuView === 'existing-field') return existingFieldItems;
    return rootItems;
  }, [items, normalizedQuery, activeMenuView, newFieldItems, existingFieldItems, rootItems]);

  const activeItemsKey = useMemo(() => activeItems.map(item => item.id).join('|'), [activeItems]);
  const groupedItems = useMemo(() => groupSlashItems(activeItems), [activeItems]);
  const groupedRootItems = useMemo(() => groupSlashItems(rootItems), [rootItems]);
  const groupedNewFieldItems = useMemo(() => groupSlashItems(newFieldItems), [newFieldItems]);
  const groupedExistingFieldItems = useMemo(() => groupSlashItems(existingFieldItems), [existingFieldItems]);
  const flatItems = useMemo(() => groupedItems.flatMap(group => group.items), [groupedItems]);

  const activeItemRef = useRef<HTMLButtonElement | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const lastInteractionRef = useRef<'mouse' | 'keyboard'>('mouse');

  useEffect(() => {
    setSelectedIndex(0);
  }, [activeItemsKey, normalizedQuery, activeMenuView]);

  useEffect(() => {
    if (menuView !== activeMenuView) {
      setMenuView(activeMenuView);
    }
  }, [menuView, activeMenuView]);

  useEffect(() => {
    activeItemRef.current?.scrollIntoView({ block: 'nearest' });
  }, [selectedIndex]);

  const handleSelectItem = (item: SlashItem) => {
    if (item.id === 'add-new-field') {
      if (!hasNewFieldItems) return;
      setMenuView('new-field');
      return;
    }
    if (item.id === 'use-existing-field') {
      if (!hasExistingFieldItems) return;
      setMenuView('existing-field');
      return;
    }
    command(item);
  };

  useImperativeHandle(
    ref,
    () => ({
      onKeyDown: event => {
        if (!flatItems.length) return false;
        lastInteractionRef.current = 'keyboard';

        if (event.key === 'Backspace' && !normalizedQuery && activeMenuView !== 'root') {
          setMenuView('root');
          return true;
        }

        if (event.key === 'ArrowRight' && !normalizedQuery && activeMenuView === 'root') {
          const item = flatItems[selectedIndex];
          if (item?.id === 'add-new-field' && hasNewFieldItems) {
            setMenuView('new-field');
            setSelectedIndex(0);
            return true;
          }
          if (item?.id === 'use-existing-field' && hasExistingFieldItems) {
            setMenuView('existing-field');
            setSelectedIndex(0);
            return true;
          }
        }

        if (event.key === 'ArrowLeft' && !normalizedQuery && activeMenuView !== 'root') {
          setMenuView('root');
          setSelectedIndex(0);
          return true;
        }

        if (event.key === 'ArrowDown') {
          setSelectedIndex(index => (index + 1) % flatItems.length);
          return true;
        }

        if (event.key === 'ArrowUp') {
          setSelectedIndex(index => (index - 1 + flatItems.length) % flatItems.length);
          return true;
        }

        if (event.key === 'Enter') {
          const item = flatItems[selectedIndex];
          if (item) {
            handleSelectItem(item);
            return true;
          }
        }
        return false;
      },
    }),
    [flatItems, selectedIndex, command, activeMenuView, normalizedQuery, hasNewFieldItems, hasExistingFieldItems],
  );

  if (normalizedQuery && items.length === 0) {
    return (
      <div className='slash-menu-wrapper'>
        <div className='slash-menu'>
          <div className='slash-menu__empty'>No results</div>
        </div>
      </div>
    );
  }

  if (flatItems.length === 0) {
    return null;
  }

  const renderGroupList = (groupedList: SlashItemGroup[], isActivePanel: boolean) => {
    let runningIndex = 0;
    return (
      <div className='slash-menu'>
        <div className='slash-menu__scroll' ref={isActivePanel ? scrollContainerRef : undefined}>
          {groupedList.map((group, groupIndex) => {
            let globalIndex = runningIndex;

            return (
              <div key={group.group} className='slash-menu__group'>
                <div className='slash-menu__group-title'>{group.group}</div>
                {group.items.map(item => {
                  const index = globalIndex;
                  globalIndex += 1;
                  runningIndex = globalIndex;
                  const isSelected = isActivePanel && index === selectedIndex;
                  const isSubmenuTrigger = isSubmenuTriggerItem(item.id);
                  const isOpen = resolveOpenSubmenu(activeMenuView, item.id);

                  return (
                    <button
                      key={item.id}
                      type='button'
                      ref={isSelected ? activeItemRef : null}
                      className={[
                        'slash-menu__item',
                        (isSelected || isOpen) && 'is-active',
                        isSubmenuTrigger && 'slash-menu__item--submenu',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                      onMouseDown={event => event.preventDefault()}
                      onMouseEnter={() => {
                        if (lastInteractionRef.current === 'keyboard') {
                          return;
                        }
                        if (isSubmenuTrigger) {
                          if (item.id === 'add-new-field' && !hasNewFieldItems) {
                            return;
                          }
                          if (item.id === 'use-existing-field' && !hasExistingFieldItems) {
                            return;
                          }
                          handleSelectItem(item);
                          return;
                        }
                        if (!isActivePanel && activeMenuView !== 'root') {
                          setMenuView('root');
                        }
                        if (isActivePanel || activeMenuView !== 'root') {
                          setSelectedIndex(index);
                        }
                      }}
                      onClick={() => handleSelectItem(item)}
                    >
                      <span className='slash-menu__icon'>
                        {item.icon ?? <Icon name='hero-bars-3-bottom-left' className='h-4 w-4' />}
                      </span>
                      <span className='slash-menu__label'>{item.title}</span>
                      {isSubmenuTrigger && (
                        <span className='slash-menu__chevron'>
                          <Icon name='hero-chevron-right-mini' className='h-4 w-4' />
                        </span>
                      )}
                    </button>
                  );
                })}
                {groupIndex < groupedList.length - 1 && <div className='slash-menu__divider'></div>}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const showSubmenu = !normalizedQuery && activeMenuView !== 'root';

  return (
    <div
      className='slash-menu-wrapper'
      onMouseMove={() => {
        lastInteractionRef.current = 'mouse';
      }}
    >
      <div className='slash-menu__panels'>
        {normalizedQuery
          ? renderGroupList(groupedItems, true)
          : renderGroupList(groupedRootItems, activeMenuView === 'root')}
        {showSubmenu && (
          <div className='slash-menu__panel'>
            {activeMenuView === 'new-field' && renderGroupList(groupedNewFieldItems, true)}
            {activeMenuView === 'existing-field' && renderGroupList(groupedExistingFieldItems, true)}
          </div>
        )}
      </div>
    </div>
  );
});

SlashMenu.displayName = 'SlashMenu';
